import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface PortfolioExercise {
  id: string;
  name: string;
  sets: number;
  reps: number;
  weight: number;
  position: number;
  routine_id: string;
  set_data: any[];
  exercise_rest_time: number | null;
  set_rest_time: number | null;
  video_url: string | null;
}

export interface PortfolioRoutine {
  id: string;
  name: string;
  image_url: string | null;
  created_at: string;
  exercises: PortfolioExercise[];
}

export const useTeacherRoutinesPortfolio = () => {
  const { user } = useAuth();

  return useQuery<PortfolioRoutine[]>({
    queryKey: ["teacher-routines-portfolio", user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data: routines, error: routinesError } = await supabase
        .from("routines")
        .select("id, name, image_url, created_at")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(200);

      if (routinesError) throw routinesError;
      if (!routines || routines.length === 0) return [];

      const routineIds = routines.map((r) => r.id);

      const { data: exercises, error: exercisesError } = await supabase
        .from("exercises")
        .select(
          "id, name, sets, reps, weight, position, routine_id, set_data, exercise_rest_time, set_rest_time, video_url"
        )
        .in("routine_id", routineIds)
        .order("position", { ascending: true });

      if (exercisesError) throw exercisesError;

      return routines.map((r) => ({
        ...r,
        exercises: (exercises || [])
          .filter((e) => e.routine_id === r.id)
          .map((e) => ({
            ...e,
            exercise_rest_time:
              e.exercise_rest_time !== null ? Number(e.exercise_rest_time) : null,
            set_rest_time:
              e.set_rest_time !== null ? Number(e.set_rest_time) : null,
            set_data: Array.isArray(e.set_data) ? e.set_data : [],
          })),
      }));
    },
    enabled: !!user,
    staleTime: 30000,
    gcTime: 300000,
  });
};
